import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, Eye, EyeOff, Youtube, Film, Save, X, ArrowUp, ArrowDown, GripVertical,
} from 'lucide-react';
import api from '@/api/client';

function ytThumb(videoId) {
  return videoId ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg` : '';
}

const EMPTY = { title: '', description: '', url: '', is_published: true };

// ---------- Edit modal ----------
function MediaFormModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const isEdit = Boolean(initial?.id);

  React.useEffect(() => { setForm(initial || EMPTY); }, [initial]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        description: form.description,
        url: form.url,
        is_published: form.is_published,
      };
      if (isEdit) return (await api.patch(`/admin/media/${initial.id}`, payload)).data;
      return (await api.post('/admin/media', payload)).data;
    },
    onSuccess: (data) => {
      toast.success(isEdit ? 'Video updated' : 'Video added to gallery');
      onSaved?.(data);
      onClose?.();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Could not save video.'),
  });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="media-modal">
        <div className="gradient-purple text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="font-serif text-xl">{isEdit ? 'Edit video' : 'Add video to gallery'}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white" aria-label="Close"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="p-5 space-y-4">
          <div>
            <label className="label-moj" htmlFor="m-title">Title <span className="text-red-600">*</span></label>
            <input id="m-title" className="input-moj" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Church Anniversary 2025"
              data-testid="media-form-title"/>
          </div>
          <div>
            <label className="label-moj" htmlFor="m-url">YouTube URL <span className="text-red-600">*</span></label>
            <input id="m-url" className="input-moj" required value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=XXXXXXXXXXX"
              data-testid="media-form-url"/>
            <p className="text-xs text-moj-charcoal/60 mt-1">Accepts any YouTube link (watch, youtu.be, embed, shorts, live) or an 11-character video ID.</p>
          </div>
          <div>
            <label className="label-moj" htmlFor="m-desc">Description</label>
            <textarea id="m-desc" rows={3} className="input-moj" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description shown on the media page…"
              data-testid="media-form-desc"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-moj-purple-deep">
            <input type="checkbox" checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
              data-testid="media-form-published"/>
            Publish this video (visible on public /media page)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline flex-1" disabled={mutation.isPending}>Cancel</button>
            <button type="submit" className="btn-primary flex-1" disabled={mutation.isPending} data-testid="media-form-save">
              <Save size={16}/> {mutation.isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Add video')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Main tab ----------
export default function MediaTab() {
  const qc = useQueryClient();
  const [modal, setModal] = useState({ open: false, initial: null });

  const { data: videos = [], isLoading } = useQuery({
    queryKey: ['adm-media'],
    queryFn: async () => (await api.get('/admin/media')).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['adm-media'] });

  const togglePublish = useMutation({
    mutationFn: async (v) => (await api.patch(`/admin/media/${v.id}`, { is_published: !v.is_published })).data,
    onSuccess: () => { toast.success('Updated'); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not update.'),
  });

  const del = useMutation({
    mutationFn: async (v) => (await api.delete(`/admin/media/${v.id}`)).data,
    onSuccess: () => { toast.success('Video deleted'); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not delete.'),
  });

  const reorder = useMutation({
    mutationFn: async (ids) => (await api.patch('/admin/media/reorder', { ordered_ids: ids })).data,
    onSuccess: () => invalidate(),
    onError: () => toast.error('Could not reorder.'),
  });

  const move = (i, delta) => {
    const next = [...videos];
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    reorder.mutate(next.map((v) => v.id));
  };

  const publishedCount = videos.filter((v) => v.is_published).length;
  const overCap = publishedCount > 20;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm text-moj-charcoal/70">
            <strong className="text-moj-purple-deep">{videos.length}</strong> total ·{' '}
            <strong className="text-moj-purple-deep">{publishedCount}</strong> published (page shows top 20 by order).
          </div>
          {overCap && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-block">
              You have more than 20 published videos — only the first 20 (by position) will show on /media. Unpublish or reorder to control which appear.
            </div>
          )}
        </div>
        <button className="btn-primary" onClick={() => setModal({ open: true, initial: null })} data-testid="media-add-btn">
          <Plus size={16}/> Add video
        </button>
      </div>

      {isLoading && <div className="text-center text-moj-charcoal/60 py-8">Loading…</div>}

      {!isLoading && videos.length === 0 && (
        <div className="card-moj p-10 text-center">
          <Film size={40} className="text-moj-gold-deep mx-auto mb-3"/>
          <p className="font-serif text-lg text-moj-purple-deep">No videos yet</p>
          <p className="text-sm text-moj-charcoal/70 mt-1 mb-5">Add the first video to start building your Media gallery.</p>
          <button className="btn-primary" onClick={() => setModal({ open: true, initial: null })}>
            <Plus size={16}/> Add first video
          </button>
        </div>
      )}

      <div className="space-y-2">
        {videos.map((v, i) => (
          <div key={v.id} className={`card-moj p-3 flex items-center gap-3 ${!v.is_published ? 'opacity-60' : ''}`} data-testid={`media-row-${v.id}`}>
            <div className="flex flex-col gap-0.5 text-moj-charcoal/40">
              <button aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 hover:text-moj-purple-deep disabled:opacity-30"><ArrowUp size={14}/></button>
              <GripVertical size={14}/>
              <button aria-label="Move down" onClick={() => move(i, 1)} disabled={i === videos.length - 1} className="p-0.5 hover:text-moj-purple-deep disabled:opacity-30"><ArrowDown size={14}/></button>
            </div>
            <a href={v.url} target="_blank" rel="noopener noreferrer" className="relative w-32 aspect-video rounded overflow-hidden bg-moj-purple-deep flex-shrink-0">
              <img src={ytThumb(v.video_id)} alt={v.title} className="absolute inset-0 w-full h-full object-cover"/>
            </a>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-moj-purple-deep truncate">{v.title}</div>
                {v.is_published ? (
                  <span className="text-[10px] uppercase tracking-wider bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-bold">Published</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-bold">Draft</span>
                )}
              </div>
              {v.description && <div className="text-xs text-moj-charcoal/70 line-clamp-1">{v.description}</div>}
              <div className="text-[11px] text-moj-charcoal/50 flex items-center gap-2 mt-1">
                <Youtube size={11}/> <span className="truncate">{v.video_id || v.url}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button title={v.is_published ? 'Unpublish' : 'Publish'} className="p-2 rounded hover:bg-moj-cream text-moj-purple-deep" onClick={() => togglePublish.mutate(v)} data-testid={`media-toggle-${v.id}`}>
                {v.is_published ? <Eye size={16}/> : <EyeOff size={16}/>}
              </button>
              <button title="Edit" className="p-2 rounded hover:bg-moj-cream text-moj-purple-deep" onClick={() => setModal({ open: true, initial: v })} data-testid={`media-edit-${v.id}`}>
                <Edit3 size={16}/>
              </button>
              <button title="Delete" className="p-2 rounded hover:bg-red-50 text-red-600"
                onClick={() => { if (window.confirm(`Delete "${v.title}"?`)) del.mutate(v); }}
                data-testid={`media-delete-${v.id}`}>
                <Trash2 size={16}/>
              </button>
            </div>
          </div>
        ))}
      </div>

      <MediaFormModal
        open={modal.open}
        initial={modal.initial}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={invalidate}
      />
    </div>
  );
}
